import {type ChangeEvent, type FC, useCallback, useEffect, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {selectDetails} from '~/CreateFabricInvitation/reducers/details/selectors';
import {Cohost, Phone} from '~/CreateFabricInvitation/routes/EventDetails/components';
import {scrollEventDetailsModuleIntoView} from '~/CreateFabricInvitation/utils/misc';
import {MAX_LENGTH_FULL_NAME} from '~/common/constants/users';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {selectPrimaryHostId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {parseUSPhoneNumber, validateNameOrEmail} from '~/common/utils';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';
import {Input} from '~library/atoms/Input';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {areAllValuesFalsy} from '~library/utils/utils';

interface HostDetailsOverlayProps {
  title: string;
  closeOverlay: () => void;
  isOpen: boolean;
}

const DEFAULT_ERRORS = {
  hostName: '',
  phoneNumber: '',
  cohost: {name: '', email: ''},
};

export const HostDetailsOverlay: FC<HostDetailsOverlayProps> = ({title, closeOverlay, isOpen}) => {
  const store = useStore();
  const {
    host = '',
    phoneNumber: savedPhoneNumber,
    cohost = {name: '', email: ''},
  } = useSelector(selectDetails);
  const primaryHostId = useSelector(selectPrimaryHostId);
  const [originalHost] = useState(host);
  const [hostName, setHostName] = useState<string>(host);
  const [phoneNumber, setPhoneNumber] = useState<string>(savedPhoneNumber || '');
  const [cohostData, setCohostData] = useState<{name: string; email: string}>({...cohost});
  const [shouldExpandCohost, setShouldExpandCohost] = useState(!areAllValuesFalsy(cohost));
  const [errors, setErrors] = useState(DEFAULT_ERRORS);

  const resetErrors = useCallback(() => {
    setErrors(DEFAULT_ERRORS);
  }, []);

  useEffect(() => {
    // Reset to previously saved state when opening overlay
    if (!isOpen) return;

    setHostName(host);
    setPhoneNumber(savedPhoneNumber || '');
    setCohostData({...cohost});
    setShouldExpandCohost(!areAllValuesFalsy(cohost));
    resetErrors();
  }, [cohost, host, isOpen, resetErrors, savedPhoneNumber]);

  const validateFields = useCallback(() => {
    const hostNameError = validateNameOrEmail('name', hostName, 'Oops! Host name is required.');
    const cohostNameError = validateNameOrEmail(
      'name',
      cohostData.name,
      'Oops! Co-host name is required.'
    );
    const cohostEmailError = validateNameOrEmail(
      'email',
      cohostData.email,
      'Oops! Valid email address is required.'
    );
    const phoneNumberError =
      phoneNumber && !parseUSPhoneNumber(phoneNumber).is_valid
        ? 'Oops! Valid phone number is required.'
        : '';
    const hasCohostError = Boolean(
      !areAllValuesFalsy(cohostData) && (cohostNameError || cohostEmailError)
    );

    if (hostNameError || phoneNumberError || hasCohostError) {
      setErrors({
        hostName: hostNameError,
        phoneNumber: phoneNumberError,
        ...(cohostNameError || cohostEmailError
          ? {cohost: {name: cohostNameError, email: cohostEmailError}}
          : {cohost: {name: '', email: ''}}),
      });

      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {
          preventUpdateModal: true,
        },
      });
    }

    return !hostNameError && !hasCohostError && !phoneNumberError;
  }, [cohostData, hostName, phoneNumber, store]);

  const restoreHostName = useCallback(() => {
    if (!host?.trim()) {
      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {host: originalHost},
      });
    }
  }, [host, originalHost, store]);

  const handleCohostChange = useCallback(
    (field: string, value: string) => {
      resetErrors();

      setCohostData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [resetErrors]
  );

  const removeCohost = useCallback(() => {
    setCohostData({name: '', email: ''});
    setShouldExpandCohost(false);
  }, []);

  const saveAllChanges = useCallback(() => {
    if (!validateFields()) return;

    store.dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {
        host: hostName,
        phoneNumber,
        ...(!areAllValuesFalsy(cohostData)
          ? {cohost: cohostData}
          : // biome-ignore lint/style/useNamingConvention: existing error
            {cohost: {name: '', email: ''}, host_ids: [primaryHostId], pending_host_ids: []}),
      },
    });
    setShouldExpandCohost(!areAllValuesFalsy(cohostData));
    closeOverlay();
  }, [closeOverlay, cohostData, hostName, phoneNumber, store, validateFields, primaryHostId]);

  const handleChange = useCallback(
    (value: string, callback: (val: string) => void) => {
      resetErrors();
      callback(value);
    },
    [resetErrors]
  );

  useEffect(() => {
    if (isOpen) scrollEventDetailsModuleIntoView();
  }, [isOpen]);

  return (
    <ResponsivePanel
      title={title}
      portalMode
      showCover
      isOpen={isOpen}
      onClose={closeOverlay}
      data-qa-id="host-details-overlay"
      renderMobileViewAs="bottom-sheet"
      disableOverlayClick
    >
      <div className="event-details__fill-height-container">
        <Input
          className="event-details__host"
          label="Host name"
          id="host"
          name="host"
          data-qa-id="event-host"
          error={errors.hostName}
          type="text"
          value={hostName}
          maxLength={MAX_LENGTH_FULL_NAME}
          onChange={({target: {value}}: ChangeEvent<HTMLInputElement>) =>
            handleChange(value, setHostName)
          }
          onBlur={restoreHostName}
          placeholder="Host Name(s)"
          required
          overlayClassName="create-flow__rc-tooltip"
        />
        <Phone
          value={phoneNumber}
          error={errors.phoneNumber}
          onChange={(value) => handleChange(value, setPhoneNumber)}
        />
        <Divider marginBottom={24} marginTop={24} />
        <Cohost
          cohost={cohostData}
          onChange={(field, value) =>
            handleChange(value, (updatedValue) => handleCohostChange(field, updatedValue))
          }
          errors={errors.cohost}
          removeCohost={removeCohost}
          shouldExpandCohost={shouldExpandCohost}
        />
      </div>
      <div className="create-flow__overlay-ctas">
        <Button
          type="button"
          fullWidth
          size="large"
          data-qa-id="save-host-details"
          variant="primary"
          onClick={saveAllChanges}
        >
          Save
        </Button>
      </div>
    </ResponsivePanel>
  );
};
